import {
  BaseCampaignItem,
  BrandCampaign,
  CampaignBase,
  MemberCampaign,
} from '@retail/campaign/types';
import { gridClasses, GridColDef } from '@mui/x-data-grid-premium';
import { Stack, Tooltip, Typography } from '@mui/material';
import { CheckIcon, InformationIcon } from '@shared/custom-icons';
import { BrandLogoGroup, LineClampContent, StackedTextCell } from '@retail/components';
import { CampaignStatusChip } from '../CampaignStatusChip';
import { MandatoryChip } from './MandatoryChip';
import React from 'react';
import { CampaignTFunction } from '@retail/campaign/i18n';
import dayjs from 'dayjs';
import { dataGridBoldClass } from '@shared/styles';
import { RemoveOutlined } from '@mui/icons-material';

type BaseCampaignGridColDef = GridColDef<CampaignBase<BaseCampaignItem>>;
type BrandCampaignGridColDef = GridColDef<BrandCampaign>;
type MemberCampaignGridColDef = GridColDef<MemberCampaign>;

const baseColumnProps: Partial<GridColDef> = {
  align: 'left',
  headerAlign: 'left',
  headerName: '',
  disableColumnMenu: true,
};

export const getCampaignBaseColumns = (t: CampaignTFunction) => {
  const campaignNameColumn: BaseCampaignGridColDef = {
    ...baseColumnProps,
    field: 'campaignName',
    headerName: t('campaign.columns.name'),
    minWidth: 180,
    renderCell: ({ row }) => <LineClampContent maxLines={3} content={row.name} />,
  };
  const mandatoryColumn: BaseCampaignGridColDef = {
    ...baseColumnProps,
    field: 'mandatory',
    headerName: t('campaign.columns.mandatory'),
    renderHeader: () => (
      <Stack direction="row" fontWeight={600}>
        {t('campaign.columns.mandatory')}
        <Tooltip title={t('campaign.columns.mandatoryDescription')}>
          <InformationIcon color="disabled" sx={{ ml: 1 }} />
        </Tooltip>
      </Stack>
    ),
    headerAlign: 'center',
    align: 'center',
    minWidth: 160,
    renderCell: ({ row }) => (
      <MandatoryChip
        mandatory={row.mandatory}
        label={row.mandatory ? t('campaign.columns.mandatory') : t('campaign.columns.notMandatory')}
      />
    ),
  };

  const campaignTypeColumn: BaseCampaignGridColDef = {
    ...baseColumnProps,
    field: 'campaignType',
    headerName: t('campaign.columns.campaignType'),
    valueGetter: (value) => value,
    minWidth: 180,
    renderCell: ({ row }) => (
      <Typography variant="subtitle2" component="span">
        {t(
          `campaign.${row.campaignType?.toLowerCase()}` as
            | 'campaign.central'
            | 'campaign.supplier'
            | 'campaign.optional'
            | 'campaign.local'
        )}
      </Typography>
    ),
  };
  const itemCountColumn: BaseCampaignGridColDef = {
    ...baseColumnProps,
    field: 'itemCount',
    minWidth: 110,
    sortable: false,
    cellClassName: dataGridBoldClass,
    headerName: t('campaign.columns.itemCount'),
  };
  const descriptionColumn: BaseCampaignGridColDef = {
    ...baseColumnProps,
    field: 'description',
    minWidth: 200,
    sortable: false,
    headerName: t('campaign.columns.description'),
    renderCell: ({ value }) => <LineClampContent maxLines={3} content={value} />,
  };
  const validFromValidToColumn: BaseCampaignGridColDef = {
    ...baseColumnProps,
    field: 'validFromValidTo',
    renderHeader: () => (
      <Stack>
        <Typography component="span" className={gridClasses.columnHeaderTitle}>{`${t(
          'campaign.columns.validFrom'
        )} /`}</Typography>
        <Typography component="span" className={gridClasses.columnHeaderTitle}>
          {t('campaign.columns.validTo')}
        </Typography>
      </Stack>
    ),
    minWidth: 160,
    renderCell: ({ row }) => {
      const validFrom = row.validFrom ? dayjs(row.validFrom).format('ll') : '-';
      const validTo = row.validTo ? dayjs(row.validTo).format('ll') : '-';

      return <StackedTextCell topLine={validFrom} bottomLine={validTo} />;
    },
  };
  const purchasePeriodFromPurchasePeriodToColumn: BaseCampaignGridColDef = {
    ...baseColumnProps,
    field: 'purchasePeriodFromPurchasePeriodTo',
    minWidth: 200,
    headerName: `${t('campaign.columns.purchasePeriodFrom')} / ${t(
      'campaign.columns.purchasePeriodTo'
    )}`,
    renderCell: ({ row }) => {
      const validFrom = row.purchasePeriodFrom ? dayjs(row.purchasePeriodFrom).format('ll') : '-';
      const validTo = row.purchasePeriodTo ? dayjs(row.purchasePeriodTo).format('ll') : '-';

      return <StackedTextCell topLine={validFrom} bottomLine={validTo} />;
    },
  };
  const lastUpdatedColumn: BaseCampaignGridColDef = {
    ...baseColumnProps,
    minWidth: 180,
    field: 'lastUpdated',
    headerName: t('campaign.columns.lastUpdated'),
    renderCell: ({ row }) => (
      <Typography variant="subtitle2" component="span">
        {row.lastUpdated ? dayjs(row.lastUpdated).format('ll') : '-'}
      </Typography>
    ),
  };

  return {
    campaignNameColumn,
    mandatoryColumn,
    campaignTypeColumn,
    itemCountColumn,
    descriptionColumn,
    validFromValidToColumn,
    purchasePeriodFromPurchasePeriodToColumn,
    lastUpdatedColumn,
  };
};

export const getBrandCampaignColumns = (t: CampaignTFunction) => {
  const statusColumn: BrandCampaignGridColDef = {
    ...baseColumnProps,
    field: 'status',
    headerName: t('campaign.columns.status'),
    headerAlign: 'center',
    align: 'center',
    minWidth: 180,
    renderCell: ({ row }) => <CampaignStatusChip status={row.releaseStatus} />,
  };
  const brandsColumn: BrandCampaignGridColDef = {
    ...baseColumnProps,
    field: 'brands',
    headerName: t('campaign.columns.brands'),
    sortable: false,
    minWidth: 160,
    renderCell: ({ row }) => <BrandLogoGroup brands={row.brands} max={4} />,
  };
  const updatedByColumn: BrandCampaignGridColDef = {
    ...baseColumnProps,
    field: 'lastUpdatedBy',
    minWidth: 250,
    flex: 1,
    headerName: t('campaign.columns.lastUpdatedBy'),
    renderCell: ({ row }) => (
      <Typography variant="subtitle2" component="span">
        {row.updatedBy?.fullName() ?? '-'}
      </Typography>
    ),
  };

  return {
    statusColumn,
    brandsColumn,
    updatedByColumn,
  };
};

export const getMemberCampaignColumns = (t: CampaignTFunction) => {
  const baseMandatoryColumn = getCampaignBaseColumns(t).mandatoryColumn as MemberCampaignGridColDef;

  const enrolledColumn: MemberCampaignGridColDef = {
    ...baseColumnProps,
    field: 'enrolled',
    width: 30,
    minWidth: 30,
    headerName: '',
    renderHeader: () => null,
    renderCell: ({ row }) =>
      row.enrolled ? (
        <Tooltip title={t('campaign.columns.enrolled.yes')}>
          <CheckIcon />
        </Tooltip>
      ) : (
        <Tooltip title={t('campaign.columns.enrolled.no')}>
          <RemoveOutlined />
        </Tooltip>
      ),
  };

  const mandatoryColumn: MemberCampaignGridColDef = {
    ...baseMandatoryColumn,
    renderCell: (params) => {
      if (params.row.campaignType === 'LOCAL') {
        return '-';
      }
      return baseMandatoryColumn.renderCell?.(params);
    },
  };
  return {
    enrolledColumn,
    mandatoryColumn,
  };
};
